import {
  $,
  component$,
  Resource,
  Slot,
  useResource$,
  useSignal,
  useVisibleTask$,
} from "@builder.io/qwik";
import { useAuthState } from "~/routes/layout";
import { signOut, isClerkAPIResponseError } from "~/lib/clerk";
import { useLocation } from "@builder.io/qwik-city";
import { fetchEntries, getBuilderSearchParams } from "@builder.io/sdk-qwik";
import ImageLogoHorizontal from "~/img/logo/dcm-horizontal-1.png?w=203;406&jsx";
import LogoIcon from "~/img/logo/icon";
import { cn } from "~/lib/cn";
import { Button, ButtonLink } from "./button";
import { useClerk } from "~/hooks/use-clerk";
import { LuUser } from "@qwikest/icons/lucide";

// Define the navigation item type
type NavItem = {
  id?: string; // Make id optional to match BuilderContent
  data?: {
    label?: string;
    url?: string;
    children?: {
      label: string;
      url: string;
    }[];
  };
};

// Transform Builder content to NavItem structure
const transformBuilderToNavItems = (builderContent: any[]): NavItem[] => {
  return builderContent.map((item) => ({
    id: item.id,
    data: {
      label: item.data?.label,
      url: item.data?.url,
      children: item.data?.children?.map(
        (child: { label: string; url: string }) => ({
          label: child.label,
          url: child.url,
        }),
      ),
    },
  }));
};

const NavLink = component$<{ href: string; hasChildren?: boolean }>(
  ({ href, hasChildren }) => {
    const isOpen = useSignal(false);
    const dropdownRef = useSignal<Element>();

    // Close dropdown when clicking outside
    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(({ cleanup }) => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.value &&
          !dropdownRef.value.contains(event.target as Node)
        ) {
          isOpen.value = false;
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      cleanup(() =>
        document.removeEventListener("mousedown", handleClickOutside),
      );
    });

    const toggleDropdown = $(() => {
      isOpen.value = !isOpen.value;
    });

    return (
      <div class="relative" ref={dropdownRef}>
        {hasChildren ? (
          <button
            onClick$={toggleDropdown}
            class="flex items-center gap-1 transition hover:text-black"
          >
            <Slot />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class={cn(
                "transition-transform",
                isOpen.value ? "rotate-180" : "",
              )}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        ) : (
          <a class="transition hover:text-black" href={href}>
            <Slot />
          </a>
        )}

        {/* Dropdown slot for children */}
        {hasChildren && (
          <div
            class={cn(
              "absolute -left-4 top-full z-50 mt-1 min-w-[200px] rounded-md bg-white py-2 shadow-lg ring-1 ring-black/5 transition-all",
              isOpen.value ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            <Slot name="dropdown" />
          </div>
        )}
      </div>
    );
  },
);

// Recursive component for desktop navigation
const DesktopNavItem = component$<{ item: NavItem }>(({ item }) => {
  const hasChildren = item.data?.children && item.data.children.length > 0;

  return (
    <NavLink
      key={item.id}
      href={item.data?.url || "#"}
      hasChildren={hasChildren}
    >
      {item.data?.label}

      {hasChildren && (
        <div q:slot="dropdown">
          {item.data?.children?.map((child) => (
            <a
              key={child.url}
              href={child.url || "#"}
              class="block px-4 py-2 text-sm hover:bg-gray-100"
            >
              {child.label}
            </a>
          ))}
        </div>
      )}
    </NavLink>
  );
});

// Recursive component for mobile navigation
const MobileNavItem = component$<{ item: NavItem }>(({ item }) => {
  const isSubMenuOpen = useSignal(false);
  const hasChildren = item.data?.children && item.data.children.length > 0;

  return (
    <div class="w-full">
      {hasChildren ? (
        <div>
          <button
            onClick$={() => (isSubMenuOpen.value = !isSubMenuOpen.value)}
            class="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 flex w-full items-center justify-between rounded px-5 py-4 text-sm font-medium leading-none"
          >
            <span>{item.data?.label}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class={cn(
                "transition-transform",
                isSubMenuOpen.value ? "rotate-180" : "",
              )}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          <div
            class={cn(
              "overflow-hidden transition-all duration-300",
              isSubMenuOpen.value ? "max-h-96" : "max-h-0",
            )}
          >
            <div class="pl-4">
              {item.data?.children?.map((child) => (
                <a
                  key={child.url}
                  href={child.url || "#"}
                  class="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 block rounded px-5 py-3 text-sm font-medium leading-none"
                >
                  {child.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <a
          href={item.data?.url || "#"}
          class="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 block w-full rounded px-5 py-4 text-sm font-medium leading-none"
        >
          {item.data?.label}
        </a>
      )}
    </div>
  );
});

export const Header = component$(() => {
  const auth = useAuthState();
  const { url } = useLocation();
  const clerk = useClerk();

  const isMenuOpen = useSignal(false);

  const linksResource = useResource$(() =>
    fetchEntries({
      model: "navigation-menu",
      apiKey: import.meta.env.VITE_BUILDER_API_KEY,
      options: getBuilderSearchParams(url.searchParams),
    }),
  );

  const handleSignOut = $(async () => {
    try {
      if (!clerk.value) return;
      await signOut();
      await clerk.value.signOut();
    } catch (err) {
      if (isClerkAPIResponseError(err)) {
        console.error(err.errors[0]?.longMessage || "Failed to log out");
      }
    }
  });

  return (
    <div class="relative mx-auto w-full max-w-screen-xl">
      <div class="z-20 flex w-full flex-row items-center justify-between gap-4 px-4 py-4 xl:px-0">
        <div class="z-120 relative">
          <a href="/" class="block flex flex-col items-start">
            <ImageLogoHorizontal
              style={{ height: "32px", width: "162px" }}
              class="hidden md:block md:h-[40px] md:w-[203px]"
              alt="DreamCoachMatch"
              decoding="sync"
              loading="eager"
            />
            <LogoIcon class="h-12 w-12 md:hidden" />
            <span class="hidden pl-1 text-xs italic text-gray-500 md:block">
              The World’s Coaching Marketplace
            </span>
          </a>
        </div>

        {/* Desktop Navigation */}
        <div class="text-md relative z-20 hidden grow items-center justify-end gap-8 rounded-full font-medium text-gray-800 md:flex">
          <Resource
            value={linksResource}
            onResolved={(links) => {
              const navItems = transformBuilderToNavItems(links);
              return navItems.map((link) => (
                <DesktopNavItem key={link.id} item={link} />
              ));
            }}
          />

          <ButtonLink href="/discovery/steps/1" size="sm">
            Find my dream coach
          </ButtonLink>

          {auth.value.isSignedIn && (
            <NavLink href="#" hasChildren={true}>
              <div class="text-brand-800 flex h-8 w-8 items-center justify-center rounded-full bg-gray-500/20">
                <LuUser />
              </div>

              <div q:slot="dropdown">
                <a
                  href="/settings"
                  class="block px-4 py-2 text-sm hover:bg-gray-100"
                >
                  Preferences
                </a>
                <a
                  href="/coach-discovery"
                  class="block px-4 py-2 text-sm hover:bg-gray-100"
                >
                  Coach Search
                </a>
                <button
                  onClick$={handleSignOut}
                  class="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                >
                  Log out
                </button>
              </div>
            </NavLink>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button
          onClick$={() => (isMenuOpen.value = !isMenuOpen.value)}
          class="z-120 absolute right-4 top-4 flex md:hidden"
          aria-label="Toggle menu"
          variant="ghost"
          size="icon-nav"
        >
          <div class="flex h-4 w-6 flex-col items-center justify-between">
            <span
              class={cn(
                "bg-brand-800 h-0.5 w-full rounded-full transition-transform",
                isMenuOpen.value && "translate-y-[7px] rotate-45",
              )}
            />
            <span
              class={cn(
                "bg-brand-800 h-0.5 w-full rounded-full transition-opacity",
                isMenuOpen.value && "opacity-0",
              )}
            />
            <span
              class={cn(
                "bg-brand-800 h-0.5 w-full rounded-full transition-transform",
                isMenuOpen.value && "-translate-y-[7px] -rotate-45",
              )}
            />
          </div>
        </Button>

        {/* Mobile Navigation */}
        <div
          class={cn(
            "z-100 fixed left-0 right-0 top-0 h-screen transition-all duration-300 md:hidden",
            isMenuOpen.value
              ? "pointer-events-auto bg-gray-900/80 backdrop-blur"
              : "pointer-events-none bg-transparent backdrop-blur-0",
          )}
          onClick$={() => (isMenuOpen.value = false)}
        >
          <div
            class={cn(
              "pointer-events-auto flex flex-col gap-2 rounded-b-2xl bg-white px-4 pb-8 pt-24 transition-all",
              isMenuOpen.value
                ? "translate-y-0 shadow-lg"
                : "-translate-y-full shadow-none",
            )}
            onClick$={(e) => e.stopPropagation()}
          >
            <Resource
              value={linksResource}
              onResolved={(links) => {
                const navItems = transformBuilderToNavItems(links);
                return navItems.map((link) => (
                  <MobileNavItem key={link.id} item={link} />
                ));
              }}
            />
            {auth.value.isSignedIn && (
              <>
                <a
                  href="/settings"
                  class="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 w-full rounded px-5 py-4 text-sm font-medium leading-none"
                >
                  Preferences
                </a>
                <a
                  href="/coach-discovery"
                  class="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 w-full rounded px-5 py-4 text-sm font-medium leading-none"
                >
                  Coach Search
                </a>
                <button
                  onClick$={handleSignOut}
                  class="hover:bg-brand-100 hover:text-brand-700 active:bg-brand-200 w-full rounded px-5 py-4 text-left text-sm font-medium leading-none"
                >
                  Log out
                </button>
              </>
            )}

            <div class="ml-4">
              {!auth.value.isSignedIn && (
                <ButtonLink href="/signin" size="sm">
                  Log in
                </ButtonLink>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
